import AxiosInstance from '@/api/axios';

type MintResponse = {
  token: string;
  lumenOrigin: string;
};

export const INTELLIGENCE_API_PREFIX = '/embed/api';
export const LLM_API_PREFIX = '/embed/llm';

// Refresh this long before actual expiry so an in-flight request never races
// a token that's about to die.
const REFRESH_SKEW_MS = 60_000;

let lumenToken: string | null = null;
let lumenOrigin: string | null = null;
let lumenTokenExpiresAt = 0;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let mintInFlight: Promise<MintResponse | null> | null = null;
let mintInFlightClientId: string | null = null;
let currentClientId: string | null = null;

function decodeExpMs(jwt: string): number {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return 0;
    const body = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = body + '==='.slice((body.length + 3) % 4);
    const json = JSON.parse(atob(padded)) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleRefresh(clientId: string) {
  clearRefreshTimer();
  const delay = Math.max(0, lumenTokenExpiresAt - Date.now() - REFRESH_SKEW_MS);
  refreshTimer = setTimeout(() => {
    void mintLumenToken(clientId);
  }, delay);
}

/**
 * Mint (or re-mint) a Lumen-scoped token for the given client. Single-flight
 * per clientId — concurrent callers for the SAME client share one in-flight
 * mint. A different clientId always starts a fresh mint (switching clients
 * invalidates the old token's scope), matching the "superseded request"
 * behavior the iframe version had via requestCounterRef.
 */
export async function mintLumenToken(clientId: string): Promise<MintResponse | null> {
  if (mintInFlight && mintInFlightClientId === clientId) return mintInFlight;
  mintInFlightClientId = clientId;

  mintInFlight = (async () => {
    try {
      const response = await AxiosInstance.get<MintResponse>('/iframe/lumen-token', {
        params: { client: clientId },
      });
      const token = response.data?.token;
      const origin = response.data?.lumenOrigin;
      if (!token || !origin) {
        lumenToken = null;
        lumenOrigin = null;
        lumenTokenExpiresAt = 0;
        return null;
      }
      currentClientId = clientId;
      lumenToken = token;
      lumenOrigin = origin;
      // Fall back to a conservative 60min if exp is somehow unparsable —
      // matches the old TOKEN_TTL_MS guess, but we prefer the real claim.
      lumenTokenExpiresAt = decodeExpMs(token) || Date.now() + 60 * 60 * 1000;
      scheduleRefresh(clientId);
      return { token, lumenOrigin: origin };
    } catch {
      lumenToken = null;
      lumenOrigin = null;
      lumenTokenExpiresAt = 0;
      return null;
    } finally {
      mintInFlight = null;
      mintInFlightClientId = null;
    }
  })();

  return mintInFlight;
}

export async function ensureLumenToken(clientId: string): Promise<MintResponse | null> {
  if (
    lumenToken &&
    lumenOrigin &&
    currentClientId === clientId &&
    Date.now() < lumenTokenExpiresAt - REFRESH_SKEW_MS
  ) {
    return { token: lumenToken, lumenOrigin };
  }
  return mintLumenToken(clientId);
}

export async function lumenFetch(
  clientId: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const attempt = async (token: string, origin: string) =>
    fetch(`${import.meta.env.VITE_LUMEN_BACKEND_API}${path}`, {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });

  let session = await ensureLumenToken(clientId);
  if (!session) {
    return new Response(null, { status: 401, statusText: 'No Lumen token' });
  }

  let res = await attempt(session.token, session.lumenOrigin);
  if (res.status === 401) {
    session = await mintLumenToken(clientId);
    if (session) res = await attempt(session.token, session.lumenOrigin);
  }
  return res;
}

/**
 * Ensure a fresh token for whichever client currently has an active session.
 * For callers hoisted above the per-client chat view (e.g. a global PDF
 * export provider) that don't know the clientId themselves and just want
 * "keep using whatever client is already active." Returns null if no
 * session has ever been established yet.
 */
export async function ensureActiveLumenToken(): Promise<MintResponse | null> {
  if (!currentClientId) return null;
  return ensureLumenToken(currentClientId);
}

/** Clear the in-memory session (e.g. on Control Centre logout). */
export function clearLumenToken(): void {
  lumenToken = null;
  lumenOrigin = null;
  lumenTokenExpiresAt = 0;
  currentClientId = null;
  clearRefreshTimer();
}
